import styled from '@emotion/styled';
import { Grid, Link, Typography, lighten, darken } from '@mui/material';
import { getLineageMetaTitle } from '@/_utils/utils';
import TaskType from '@/components/_common/Tasks/TaskType';
import DateTimeDisplay from '@/components/_common/DateTimeDisplay';
import LabelValue from '@/components/_common/LabelValue';
import PolarisIcon from '@/components/_common/PolarisIcon';
import TableStatus from '@/components/_common/Table/TableStatus';
import TaskCardLink from './TaskCardLink';
import AssigneeSelect from '@/components/_common/AssigneeSelect/AssigneeSelect';
import { getAgeFromDate } from '@/_utils/date';

interface TaskCardProps {
  data: any;
  viewFront: boolean;
  subtitle?: string;
}

const TaskCard = ({ data, viewFront = true, subtitle }: TaskCardProps) => {
  return (
    <FlipCard view={viewFront ? 'front' : 'back'}>
      <FlipCardInner view={viewFront ? 'front' : 'back'}>
        <FrontCard>
          <TopCard>
            <Grid container justifyContent="space-between">
              <div style={{ marginBottom: 10 }}>
                <TaskType type={data.step} prettyName={data.pretty_name} />
              </div>
              <TableStatus status={data.status} dotOnly />
            </Grid>
            <Typography fontSize={'1.1rem'}>
              <strong>{data.job.title.name}</strong>
            </Typography>
            <Typography variant="caption" sx={{ mt: 0.5 }}>
              {subtitle ? subtitle : getLineageMetaTitle(data.job.title).split(':')[0]}
            </Typography>
          </TopCard>
          <BottomCard>
            <Grid container alignItems="center">
              <PolarisIcon icon="calendar" height={20} style={{ opacity: 0.5 }} />
              <Typography style={{ paddingLeft: 5, whiteSpace: 'nowrap', opacity: 0.7 }}>Due Date:</Typography>
              <div style={{ paddingLeft: 8 }}>
                <DateTimeDisplay singleLine>{data.job.due_date}</DateTimeDisplay>
              </div>
              <TaskCardLink task={data} />
            </Grid>
          </BottomCard>
        </FrontCard>
        <BackCard>
          <BackCardContent>
            <Grid container justifyContent="space-between" spacing={2}>
              <LabelValue label="Task ID" size={{ xs: 4 }}>
                {data.hid}
              </LabelValue>
              <LabelValue label="Status" size={{ xs: 5 }}>
                <TableStatus status={data.status} sx={{ '& span': { whiteSpace: 'normal' } }} />
              </LabelValue>
              <LabelValue label="Related Job" size={{ xs: 3 }}>
                <Link href={`/jobs/${data.job?.id}`} target="_blank" rel="noreferrer" component="a">
                  J{data.job?.id}
                </Link>
              </LabelValue>

              <LabelValue label="Updated at" size={{ xs: 4 }}>
                <DateTimeDisplay>{data.updated_at}</DateTimeDisplay>
              </LabelValue>
              <LabelValue label="Age" size={{ xs: 3 }}>
                {getAgeFromDate(data.created_at)}
              </LabelValue>

              <LabelValue label="Assignee" size={{ xs: 5 }}>
                <AssigneeSelect popper value={data.assigned_user} id={data.id} update="task" assigns="user" />
              </LabelValue>
            </Grid>
          </BackCardContent>
        </BackCard>
      </FlipCardInner>
    </FlipCard>
  );
};

export default TaskCard;

const FlipCard = styled.div<{ view: 'front' | 'back' }>`
  background: transparent;
  height: 200px;
  width: 100%;
  perspective: 1000px;
  transition: transform 0.8s;
  transform: rotateY(${({ view }) => (view === 'back' ? '180deg' : '0deg')});
  text-align: left;
`;

const FlipCardInner = styled.div<{ view: 'front' | 'back' }>`
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.8s;
  transform-style: preserve-3d;
  transform: rotateY(${({ view }) => (view === 'back' ? '180deg' : '0deg')});
`;

const FrontCard = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  -webkit-backface-visibility: hidden; /* Safari */
  backface-visibility: hidden;

  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  background: ${({ theme }) => theme.palette.background.default};
  transition: all 0.3s ease-in-out;
  transform-style: preserve-3d;
  backface-visibility: hidden;

  &:hover {
    transform: scale(1.05);
  }
`;

const BackCard = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  -webkit-backface-visibility: hidden; /* Safari */
  backface-visibility: hidden;
  transform: rotateY(180deg);
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  background: ${({ theme }) => theme.palette.background.default};

  .assignee-select {
    width: 100%;

    .display-wrapper {
      width: 100%;

      #display {
        width: 100%;

        span {
          white-space: nowrap;
          max-width: 100%;
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }
    }
  }
`;

const BackCardContent = styled.div`
  transform: rotateY(180deg);
  padding: ${({ theme }) => theme.spacing(3)};
  column-gap: ${({ theme }) => theme.spacing(3)};
`;

const TopCard = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
  display: flex;
  flex-direction: column;
`;

const BottomCard = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: ${({ theme }) => theme.spacing(1.5, 2)};
  border-radius: ${({ theme }) => `0 0 ${theme.shape.borderRadius}px  ${theme.shape.borderRadius}px`};
  background: ${({ theme }) =>
    theme.palette.mode === 'dark' ? lighten(theme.palette.background.paper, 0.1) : darken(theme.palette.background.paper, 0.1)};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  opacity: 0.7;
`;
